/* ---------------------------------------------------------------------------
   NAV-01 / CMP-03 - Desktop menu keyboard behaviour.
   WCAG 2.2 SC 3.2.1 On Focus, 2.1.1 Keyboard, 4.1.2 Name Role Value.

   Shared by DropdownMenu and MegaMenu below.

   Three defects are addressed:

     1. Both classes bound `focusin` to the same handler as `mouseenter`, so
        simply tabbing onto a top-level item expanded its submenu. Merely moving
        focus must not change context. `focusin` is gone; the pointer path
        (`mouseenter`) is untouched.

     2. Nothing exposed expanded state. The trigger now reports `aria-expanded`,
        kept in step with the `is-active` class on every open and close path,
        including the ones driven by hover.

     3. There was no keyboard way in or out. Closing happened only on a real mouse
        `mouseleave`, so a keyboard user who somehow opened the menu could not
        close it and could not reach the submenu links deliberately. ArrowDown (or
        Alt+ArrowDown) now opens and enters the submenu, Escape closes and returns
        focus to the trigger, and focus leaving the component closes it.

   The trigger deliberately remains a link. It has a real destination, so Enter
   navigates - unambiguous, and unchanged for existing users. The APG-preferred
   arrangement is a link plus a separate caret <button>; that is a markup and CSS
   change that needs visual QA in a preview theme, and is recorded as follow-up
   work rather than made blind.
   --------------------------------------------------------------------------- */

/*
  These components nest - a <dropdown-menu> panel contains more <dropdown-menu>
  elements, each with its own caret and its own panel - so each one has to find
  *its own* parts and not reach into a child's.

  Walking the direct children says that plainly. The caret is either a direct
  child or sits inside the top-level <a>, so both shapes are handled and the
  markup can change without this breaking.

  Note on the nested shape: a <button> inside an <a> is invalid HTML, and the
  browser treats a click on the caret as a click on the link, so the page
  navigates while the panel opens. The click handler below calls preventDefault(),
  and because that runs on the same event as it bubbles up, it cancels the
  anchor's navigation too. That is the only thing holding this arrangement
  together: if the handler ever fails to attach, the caret silently becomes a
  second link to the parent page.
*/
function findOwnToggle(host) {
  for (const child of host.children) {
    if (child.classList.contains('header__submenu-toggle')) return child;

    if (child.tagName === 'A') {
      const nested = child.querySelector('.header__submenu-toggle');
      if (nested) return nested;
    }
  }
  return null;
}

function findOwnPanel(host) {
  for (const child of host.children) {
    if (child.classList.contains('list-menu-dropdown')) return child;
  }
  return null;
}

function menuDisclosureMixin(instance) {
  instance.trigger = findOwnToggle(instance);
  instance.submenu = instance.trigger ? findOwnPanel(instance) : null;

  if (!instance.trigger || !instance.submenu) return;

  instance.setExpanded = (expanded) => {
    instance.trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  };

  /*
    Focus stays on the toggle after expanding, as in the APG disclosure pattern.
    The panel is the next thing in DOM order, so Tab walks straight into it and
    Shift+Tab comes back out - no scripted focus moves to fight with.

    The reveal is forced to its settled state instead of following the pointer
    path, which staggers `is-active` behind a 100-200ms hover-intent delay and
    `show-from-bottom` (what actually lifts the list off opacity 0) another 150ms
    after that. Tabbing in during that window would land focus on something not
    yet visible.
  */
  instance.expandNow = () => {
    instance.handleMouseover();

    clearTimeout(instance.timeout);
    instance.classList.add(instance.classes.active);
    instance.setExpanded(true);

    instance
      .querySelectorAll(instance.selectors.childAnimate)
      .forEach((element) => element.classList.add(instance.classes.animate));
  };

  instance.closeFromKeyboard = () => {
    instance.reset();
    instance.resetBackground();
    if (instance.header) instance.header.classList.remove(instance.classes.hover);
    instance.trigger.focus();
  };

  /*
    A real button, so Enter and Space arrive here as a click with no extra
    handling. That matters more than it looks: the earlier draft opened the panel
    on ArrowDown, and NVDA and JAWS swallow the arrow keys in browse mode for
    virtual-cursor navigation, so the keydown never reached the page. Enter is
    passed through in browse mode, which is why the disclosure has to be a button.
  */
  instance.trigger.addEventListener('click', (event) => {
    event.preventDefault();
    instance.header = instance.header || document.getElementById(instance.selectors.header);

    if (instance.classList.contains(instance.classes.active)) {
      instance.reset();
      instance.resetBackground();
      if (instance.header) instance.header.classList.remove(instance.classes.hover);
    } else {
      instance.expandNow();
    }
  });

  instance.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (!instance.classList.contains(instance.classes.active)) return;
    event.stopPropagation();
    instance.closeFromKeyboard();
  });

  // Tabbing out of the component closes it. Deferred by a tick because during a
  // focus change document.activeElement is momentarily <body>.
  instance.addEventListener('focusout', () => {
    setTimeout(() => {
      if (!instance.contains(document.activeElement) && instance.classList.contains(instance.classes.active)) {
        instance.reset();
        instance.resetBackground();
      }
    });
  });
}

class DropdownMenu extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hover: 'is-hover',
      active: 'is-active',
      animate: 'show-from-bottom',
    };
    this.selectors = {
      header: 'shopify-section-header',
      background: '.header-background',
      topMenuItem: '.header__menu-item--top',
      menus: 'dropdown-menu, mega-menu',
      wrapper: '.list-menu',
      childAnimate: '.list-menu-dropdown > .list-menu'
    };
    this.timeout;

    // `focusin` intentionally not bound here - see the note above.
    this.addEventListener('mouseenter', this.handleMouseover.bind(this));

    menuDisclosureMixin(this);
  }

  handleMouseover() {
    if (this.classList.contains(this.classes.active)) return;

    this.bindEvent();
    this.removeClasses();
    this.addClasses();
    this.resetBackground();
    this.animateMenu();
  }

  handleMouseleave() {
    if (!this.header) return;

    this.reset();
    this.resetBackground();

    this.header.classList.remove(this.classes.hover);
    this.wrapper.removeEventListener('mouseleave', this.onHeaderMouseLeaveEvent);
  }

  bindEvent() {
    this.onHeaderMouseLeaveEvent = this.onHeaderMouseLeaveEvent || this.handleMouseleave.bind(this);

    this.header = this.header || document.getElementById(this.selectors.header);

    this.wrapper = this.closest(this.selectors.wrapper)
    this.wrapper.addEventListener('mouseleave', this.onHeaderMouseLeaveEvent);
  }

  addClasses() {
    const isTopLevel = this.querySelector(this.selectors.topMenuItem);

    let delay = 0;
    if (isTopLevel && !this.isHover()) {
      delay = 100; // Reduced from 150ms for faster response

      if (this.closest('.header--top-center') || this.closest('.header--top-left')) {
        delay = 200;
      }
    }
    this.timeout = setTimeout(() => {
      if (this.isHover()) {
        this.classList.add(this.classes.active);
        if (this.setExpanded) this.setExpanded(true);
      }
    }, delay);

    this.header.classList.add(this.classes.hover);
  }

  removeClasses() {
    const menus = this.header.querySelectorAll(this.selectors.menus);

    menus.forEach((menu) => {
      let found = false;
      const childMenus = menu.querySelectorAll('dropdown-menu');
      childMenus.forEach((element) => {
        if (element === this) {
          found = true;
          return;
        }
      });

      if (!found) menu.reset();
    });
  }

  isHover() {
    return this.header.classList.contains(this.classes.hover);
  }

  reset() {
    this.classList.remove(this.classes.active);
    if (this.setExpanded) this.setExpanded(false);

    const childAnimate =  this.querySelector(this.selectors.childAnimate);
    if (childAnimate) childAnimate.classList.remove(this.classes.animate);

    clearTimeout(this.timeout);
  }

  resetBackground() {
    // Null-guarded: .header-background is a single decorative <span> in the header
    // section, and any of the four A/B testing tools on this theme can remove it.
    // An exception here used to break all further menu interaction.
    if (!this.header) return;
    const background = this.header.querySelector(this.selectors.background);
    if (background) background.classList.remove(this.classes.active);
  }

  animateMenu() {
    const childAnimate =  this.querySelector(this.selectors.childAnimate);
    if (childAnimate) {
      setTimeout(() => {
        childAnimate.classList.add(this.classes.animate);
      }, 150);
    }
  }

  close() {
    this.handleMouseleave();
  }
}
customElements.define('dropdown-menu', DropdownMenu);

class MegaMenu extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hover: 'is-hover',
      active: 'is-active',
      animate: 'show-from-bottom',
    };
    this.selectors = {
      header: 'shopify-section-header',
      background: '.header-background',
      dropdown: '.list-menu-dropdown',
      menus: 'dropdown-menu, mega-menu',
      wrapper: '.list-menu',
      childAnimate: '.mega-menu__item, .mega-menu__promo',
      navLink: '.megamenu__nav-link--product',
      navLinkImage: '.megamenu__nav-link-image',
      stickyHeader: '.header-wrapper'
    };
    this.timeout = [];
    this.closeTimeout = null;
    this.resizeObserver = null;

    // `focusin` intentionally not bound here - see the NAV-01 note above.
    this.addEventListener('mouseenter', this.handleMouseover.bind(this));

    this.initResizeObserver();
    menuDisclosureMixin(this);
  }

  initResizeObserver() {
    this.resizeObserver = new ResizeObserver((entries) => {
      if (this.classList.contains(this.classes.active)) {
        requestAnimationFrame(() => {
          this.checkAllImagePositions();
        });
      }
    });
  }

  setMenuItemWidth() {
    const menuItem = this.querySelector('.header__menu-item--top');
    if (!menuItem) return;

    /*
      --width-link sizes the invisible ::after bridge that carries the mouse
      across the gap between the menu item and the panel. While the caret was
      nested inside the link, the link's own width included it. With the caret
      as a sibling (FND-62) the link alone is narrower, the bridge stopped
      short of the caret, and a straight descent from the caret hit bare
      header and closed the menu 12px above the panel - found by the client.
      The union of link + caret restores the full span.
    */
    const toggle = this.querySelector('.header__submenu-toggle');
    let width = menuItem.getBoundingClientRect().width;
    if (toggle) {
      const a = menuItem.getBoundingClientRect();
      const t = toggle.getBoundingClientRect();
      width = Math.max(a.right, t.right) - Math.min(a.left, t.left);
    }
    this.style.setProperty('--width-link', `${width}px`);
  }

  handleMouseover() {
    if (this.classList.contains(this.classes.active)) return;
    
    // Clear any pending close timeout
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
    
    this.setMenuItemWidth();
    this.bindEvent();
    this.removeClasses();
    this.addClasses();
    this.showSublist();
  }

  handleMouseleave() {
    if (!this.header) return;

    // Add delay before closing to allow cursor to reach dropdown
    this.closeTimeout = setTimeout(() => {
      this.reset();
      this.resetBackground();

      this.header.classList.remove(this.classes.hover);
      this.wrapper.removeEventListener('mouseleave', this.onHeaderMouseLeaveEvent);
    }, 150);
  }

  bindEvent() {
    this.onHeaderMouseLeaveEvent = this.onHeaderMouseLeaveEvent || this.handleMouseleave.bind(this);
    
    this.header = this.header || document.getElementById(this.selectors.header);

    this.wrapper = this.closest(this.selectors.wrapper)
    this.wrapper.addEventListener('mouseleave', this.onHeaderMouseLeaveEvent);
  }

  showSublist() {
    if (!this.header) return;
    const background = this.header.querySelector(this.selectors.background);
    if (background) background.classList.add(this.classes.active);
  }

  addClasses() {
    this.classList.add(this.classes.active);
    if (this.setExpanded) this.setExpanded(true);

    const childAnimate = this.querySelectorAll(this.selectors.childAnimate);
    const delay = childAnimate.length > 5 ? 75 : 150;
    childAnimate.forEach((element, index) => {
      const timeout = setTimeout(() => {
        element.classList.add(this.classes.animate);
      }, (delay * index) + 100);
      
      this.timeout.push(timeout);
    });
    
    this.header.classList.add(this.classes.hover);
    
    const isInDrawer = this.closest('header-drawer');
    if (!isInDrawer) {
      const stickyHeader = document.querySelector(this.selectors.stickyHeader);
      if (stickyHeader) {
        stickyHeader.classList.add('mega-menu-open');
      }
    }
    
    requestAnimationFrame(() => {
      this.checkAllImagePositions();
    });
    
    const dropdown = this.querySelector(this.selectors.dropdown);
    if (dropdown && this.resizeObserver) {
      this.resizeObserver.observe(dropdown);
    }
  }

  removeClasses() {
    const menus = this.header.querySelectorAll(this.selectors.menus);
    menus.forEach((menu) => {
      menu.reset();
    });
  }

  reset() {
    this.classList.remove(this.classes.active);
    if (this.setExpanded) this.setExpanded(false);

    const childAnimate = this.querySelectorAll(this.selectors.childAnimate);
    childAnimate.forEach((element) => {
      element.classList.remove(this.classes.animate);
    });
    this.timeout.forEach((timeout) => {
      clearTimeout(timeout);
    });
    
    // Clear close timeout if exists
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
    
    const images = this.querySelectorAll(this.selectors.navLinkImage);
    images.forEach((image) => {
      image.classList.remove('megamenu__nav-link-image--top');
    });
    
    const isInDrawer = this.closest('header-drawer');
    if (!isInDrawer) {
      const stickyHeader = document.querySelector(this.selectors.stickyHeader);
      if (stickyHeader) {
        stickyHeader.classList.remove('mega-menu-open');
      }
    }
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  resetBackground() {
    // Null-guarded: .header-background is a single decorative <span> in the header
    // section, and any of the four A/B testing tools on this theme can remove it.
    // An exception here used to break all further menu interaction.
    if (!this.header) return;
    const background = this.header.querySelector(this.selectors.background);
    if (background) background.classList.remove(this.classes.active);
  }

  checkAllImagePositions() {
    const navLinks = this.querySelectorAll(this.selectors.navLink);
    const dropdown = this.querySelector(this.selectors.dropdown);
    
    if (!dropdown) return;
    
    const dropdownRect = dropdown.getBoundingClientRect();
    
    navLinks.forEach((link) => {
      const image = link.querySelector(this.selectors.navLinkImage);
      if (!image) return;
      
      const linkRect = link.getBoundingClientRect();
      const imageBottom = linkRect.bottom;
      const imageTop = imageBottom - 165;
      const isOverflowingTop = imageTop < dropdownRect.top;
      
      if (isOverflowingTop) {
        image.classList.add('megamenu__nav-link-image--top');
      } else {
        image.classList.remove('megamenu__nav-link-image--top');
      }
    });
  }

  close() {
    this.handleMouseleave();
  }
}
customElements.define('mega-menu', MegaMenu);
